'use client';

import { useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import type { EmailSignInResponse } from '@mentorivo/shared';
import { toLocale } from '@mentorivo/shared/locales';

import {
  AuthBrand,
  AuthDivider,
  AuthField,
  FieldError,
  FormAlert,
  GoogleButton,
  SubmitButton,
  fieldV,
  legalChunks,
} from '@/components/auth-parts';
import { EmailCodeStep } from '@/components/email-code-step';
import { ApiError } from '@/lib/api';
import { afterSignInPath, returnPathFromLocation } from '@/lib/auth-return';
import { Link, useRouter } from '@/i18n/navigation';
import { dur, ease, sceneContainer, sceneItem, stagger } from '@/lib/motion';
import { startEmailSignIn } from '@/lib/session';
import { siteLinks } from '@/lib/site-links';

/**
 * Kayıt formu — bölünmüş tuvalin beyaz yarısı.
 *
 * Giriş formunun kardeşi; tek farkı AD sorması (10 Eki 2026'dan beri parola
 * yok). İki adım:
 *
 *   1. ad + iş e-postası → "E-posta ile devam et"
 *   2. kod (`EmailCodeStep`) — kod doğrulanınca hesap AÇILIYOR
 *
 * Hesap başlatmada değil doğrulamada açılıyor: yazım hatalı bir adres,
 * kimsenin tamamlayamayacağı bir doğrulamada kalıyor ve sahipsiz hesap
 * birikmiyor. Adres zaten kayıtlıysa aynı kod kişiyi kendi hesabına sokuyor
 * (ad yok sayılıyor) — "bu adres kayıtlı" uyarısı bu yüzden yok; sunucu
 * kimin kayıtlı olduğunu söylemiyor.
 *
 * Daha fazlası (şirket, telefon, çalışma alanı) kayıt anında sorulmuyor —
 * hesap açtıktan sonra sorulabilecek her şey kurulum sihirbazında. Kayıt
 * ekranı bir form değil bir eşik; ne kadar kısaysa o kadar iyi.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 * Giriş ekranıyla aynı dil: açılışta okunma sırasıyla odaklanma, adım
 * YERİNDE değişiyor (`mode="wait"`), yasal satır kayarak yer değiştiriyor.
 */

/** Kabaca "@ var, nokta var, boşluk yok". Asıl doğrulama sunucuda. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const stepV: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: dur.instant, staggerChildren: stagger.base, delayChildren: 0.04 },
  },
  exit: { opacity: 0, y: -8, filter: 'blur(4px)', transition: { duration: dur.instant, ease: ease.in } },
};

type Errors = { name?: string; email?: string; form?: string };
type Sent = { email: string; name: string; resendInSeconds: number };

export function RegisterForm() {
  const t = useTranslations('register');
  const uid = useId();
  const router = useRouter();
  /* Kayıt anındaki arayüz dili hesaba yazılıyor: kod postası ve sonraki
     oturumlar bu dilde. Tarayıcı dilini tahmin etmek yerine kişinin o an
     baktığı sayfanın dili daha doğru bir sinyal (13 dil). */
  const locale = useLocale();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<Sent | null>(null);
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [tried, setTried] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  function validate(next = { name, email }): Errors {
    const found: Errors = {};
    if (next.name.trim().length < 2) found.name = t('name.error');
    if (!EMAIL.test(next.email.trim())) found.email = t('email.error');
    return found;
  }

  function onChange(field: 'name' | 'email', value: string) {
    const next = { name, email, [field]: value };
    if (field === 'name') setName(value);
    else setEmail(value);
    if (tried) setErrors(validate(next));
  }

  async function start(address: string, fullName: string) {
    const result = await startEmailSignIn({
      email: address,
      name: fullName,
      locale: toLocale(locale),
      next: returnPathFromLocation() ?? undefined,
    });
    if (!result.sent) throw new Error('not-sent');
    return result;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      /* Odak İLK hatalı alana gider — yoksa klavye kullanıcısı, ekranın
         yukarısında beliren uyarıyı ancak geri gezinerek buluyor. */
      (found.name ? nameRef : emailRef).current?.focus();
      return;
    }

    setErrors({});
    setPending(true);

    const address = email.trim().toLowerCase();
    const fullName = name.trim();
    try {
      const result = await start(address, fullName);
      setSent({ email: address, name: fullName, resendInSeconds: result.resendInSeconds });
    } catch (err) {
      setErrors({
        form:
          err instanceof ApiError && err.status === 429
            ? t('tooMany')
            : err instanceof Error && err.message === 'not-sent'
              ? t('notSent')
              : t('notConnected'),
      });
    } finally {
      setPending(false);
    }
  }

  function onSignedIn(session: EmailSignInResponse) {
    router.push(afterSignInPath(session, returnPathFromLocation()));
  }

  const nameId = `${uid}-name`;
  const emailId = `${uid}-email`;

  return (
    <section className="auth-col">
      <motion.div
        className="auth-form-inner"
        variants={sceneContainer}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={sceneItem}>
          <AuthBrand />
        </motion.div>

        <motion.div className="auth-swap" layout="position">
          <AnimatePresence mode="wait">
            {sent === null ? (
              <motion.div key="details" variants={stepV} initial="hidden" animate="show" exit="exit">
                <motion.div className="auth-head" variants={sceneItem}>
                  <h1 className="auth-title">{t('title')}</h1>
                  <p className="auth-lead">{t('lead')}</p>
                </motion.div>

                <motion.div variants={sceneItem}>
                  <GoogleButton label={t('google')} next={returnPathFromLocation() ?? undefined} />
                </motion.div>

                <motion.div variants={sceneItem}>
                  <AuthDivider label={t('divider')} />
                </motion.div>

                <form className="auth-form" onSubmit={onSubmit} noValidate>
                  {/* İlk alan: üstünde konumu değişen bir şey yok, `layout`
                      gerekmiyor — tam `sceneItem` (y + blur) ile girebiliyor. */}
                  <motion.div className="auth-field" variants={sceneItem}>
                    <label className="auth-label" htmlFor={nameId}>
                      {t('name.label')}
                    </label>
                    <AuthField
                      invalid={Boolean(errors.name)}
                    >
                      <input
                        ref={nameRef}
                        id={nameId}
                        className="auth-input"
                        type="text"
                        name="name"
                        autoComplete="name"
                        placeholder={t('name.placeholder')}
                        value={name}
                        onChange={(e) => onChange('name', e.target.value)}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? `${nameId}-error` : undefined}
                      />
                    </AuthField>
                    <FieldError id={`${nameId}-error`} message={errors.name} />
                  </motion.div>

                  {/* Buradan aşağısı `layout="position"`: üstteki hata
                      belirince KAYARAK yer değiştiriyorlar. */}
                  <motion.div className="auth-field" variants={fieldV} layout="position">
                    <label className="auth-label" htmlFor={emailId}>
                      {t('email.label')}
                    </label>
                    <AuthField
                      invalid={Boolean(errors.email)}
                    >
                      <input
                        ref={emailRef}
                        id={emailId}
                        className="auth-input"
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        placeholder={t('email.placeholder')}
                        value={email}
                        onChange={(e) => onChange('email', e.target.value)}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? `${emailId}-error` : undefined}
                      />
                    </AuthField>
                    <FieldError id={`${emailId}-error`} message={errors.email} />
                  </motion.div>

                  <FormAlert message={errors.form} />

                  <motion.div variants={fieldV} layout="position">
                    <SubmitButton
                      pending={pending}
                      label={t('submit')}
                      pendingLabel={t('submitting')}
                    />
                  </motion.div>
                </form>

                <motion.p className="auth-alt" variants={sceneItem}>
                  {t('haveAccount')}{' '}
                  <Link href={siteLinks.login.href} className="auth-alt-link">
                    {t('login')}
                  </Link>
                </motion.p>
              </motion.div>
            ) : (
              <motion.div key="code" variants={stepV} initial="hidden" animate="show" exit="exit">
                <EmailCodeStep
                  email={sent.email}
                  initialResendSeconds={sent.resendInSeconds}
                  onResend={() => start(sent.email, sent.name)}
                  onBack={() => {
                    setSent(null);
                    setErrors({});
                  }}
                  onSignedIn={onSignedIn}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.p className="auth-legal" variants={sceneItem} layout="position">
          {t.rich('legal', legalChunks)}
        </motion.p>
      </motion.div>
    </section>
  );
}
